import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Navbar";
import { Search, X } from "lucide-react";
import { ReactComponent as FlooronLogo } from "../Assets/Flooron.svg";
import "./HeaderShell.css";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Category } from "../api.response";

const HeaderShell: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [searchValue, setSearchValue] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.getCategories();
        setCategories(response.data ?? []);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };

    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.title.toLowerCase().includes(searchValue.toLowerCase())
  );

  const resetSearch = () => {
    setSearchValue("");
    setShowSearch(false);
  };

  const handleSearchSubmit = () => {
    const query = searchValue.trim();

    if (!query) {
      navigate('/search');
      resetSearch();
      return;
    }

    const matchedCategory = categories.find(
      (category) => category.title.toLowerCase() === query.toLowerCase()
    );

    if (matchedCategory) {
      navigate(`category/${matchedCategory.id.toString()}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }

    resetSearch();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleCategorySelect = (category: Category) => {
    navigate(`category/${category.id.toString()}`);
    resetSearch();
  };

  return (
    <div className="header-fixed-container">
      <header className={`header-shell ${isMobile ? "mobile-mode" : "desktop-mode"}`}>
        {!isMobile ? (
          <>
            <div className="header-section left">
              <div className="island logo-island">
                <FlooronLogo className="logo-svg" />
              </div>
            </div>
            <div className="header-section center">
              <Navbar isLoggedIn={isLoggedIn} />
            </div>
            <div className="header-section right">
              <div className="island search-island">
                <div className={`search-wrapper ${showSearch ? "active" : ""}`}>
                  <input
                    ref={inputRef}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    type="text"
                    placeholder="Search categories or products..."
                    className="search-input"
                  />
                  <button
                    className="search-btn"
                    onClick={() => setShowSearch(!showSearch)}
                    aria-label={showSearch ? "Close search" : "Open search"}
                  >
                    {showSearch ? <X size={20} /> : <Search size={20} />}
                  </button>

                  {showSearch && (
                    <div className="search-suggestions">
                      {categories.length === 0 ? (
                        <div className="search-no-results">
                          Loading categories...
                        </div>
                      ) : filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                          <button
                            key={category.id.toString()}
                            type="button"
                            className="search-suggestion"
                            onClick={() => handleCategorySelect(category)}
                          >
                            {category.title}
                          </button>
                        ))
                      ) : (
                        <div className="search-no-results">
                          No matching categories. Press Enter to search products.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="island mobile-group-island">
            <FlooronLogo className="logo-svg" />
            <div className={`search-wrapper ${showSearch ? "active" : ""}`}>
              <input
                ref={inputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyDown}
                type="text"
                placeholder="Search categories or products..."
                className="search-input"
              />
              <button
                className="search-btn"
                onClick={() => setShowSearch(!showSearch)}
                aria-label={showSearch ? "Close search" : "Open search"}
              >
                {showSearch ? <X size={18} /> : <Search size={18} />}
              </button>

              {showSearch && (
                <div className="search-suggestions">
                  {categories.length === 0 ? (
                    <div className="search-no-results">
                      Loading categories...
                    </div>
                  ) : filteredCategories.length > 0 ? (
                    filteredCategories.map((category) => (
                      <button
                        key={category.id.toString()}
                        type="button"
                        className="search-suggestion"
                        onClick={() => handleCategorySelect(category)}
                      >
                        {category.title}
                      </button>
                    ))
                  ) : (
                    <div className="search-no-results">
                      No matching categories. Press Enter to search products.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {isMobile && (
        <div className="mobile-navbar-row">
          <Navbar isLoggedIn={isLoggedIn} />
        </div>
      )}
    </div>
  );
};

export default HeaderShell;
